import React from 'react';
import Logo from './Logo';
import Icon from './Icon';
import { Link, useIdioma } from '../i18n';
import '../styles/Auth.css';

/** Ícone do Google em tons neutros — nunca a versão colorida sobre fundo escuro. */
export const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 256 262" aria-hidden="true">
    <path
      fill="#b2b6ca"
      d="M255.9 133.5c0-11.4-.9-19.7-2.9-28.3H130.5v51.2h71.4c-1.4 12.8-9.2 32-26.4 45l-.2 1.6 38.4 29.8 2.7.3c24.4-22.6 38.5-55.8 38.5-99.6"
    />
    <path
      fill="#9397ab"
      d="M130.5 261.1c34.9 0 64.2-11.5 85.6-31.3l-40.8-31.6c-10.9 7.6-25.6 12.9-44.8 12.9-29.3 0-54.2-19.3-63.1-46l-1.5.1-39.6 30.6-.5 1.5c21.3 42.3 65 63.8 104.7 63.8"
    />
    <path
      fill="#75798c"
      d="M67.4 165.1c-2.3-8.6-3.7-17.7-3.7-27.1 0-9.4 1.4-18.5 3.6-27.1l-.1-1.8-40.1-31.1-1.3.6C17.7 96.7 12.7 116.7 12.7 138s5 41.3 13.1 59.4z"
    />
    <path
      fill="#595d6c"
      d="M130.5 50.5c20.8 0 34.9 9 42.9 16.5l31.3-30.6C185.4 17.9 156.2 6 130.5 6 90.8 6 47.1 27.5 25.8 69.8l41.5 32.2c9-26.7 33.9-51.5 63.2-51.5"
    />
  </svg>
);

/* Ilustração de como uma lista de runs aparece dentro do produto. Os domínios
   são deliberadamente de exemplo — `example`, `client-x` — para ninguém ler
   isto como carteira de clientes. */
const RUNS = [
  { id: '#4172', name: 'silentpuma.com', n: '1,284', status: 'acesso.runFeita', tone: 'ok' },
  { id: '#4170', name: 'example-store.com', n: '8,902', status: 'acesso.runFeita', tone: 'ok' },
  {
    id: '#4169',
    name: 'Example Channel',
    n: '212 videos',
    status: 'acesso.runRodando',
    tone: 'warn'
  },
  { id: '#4168', name: 'client-x.com', n: '3,410', status: 'acesso.runFalhou', tone: 'crit' }
];

/* Fatos do plano Basic como ele é hoje. "No credit card" passou a ser verdade
   em backend@22f0a1b, quando o checkout deixou de pedir cartão em cobrança de
   valor zero; antes disso a frase estava na home e era falsa. */
const POINTS = [
  { chave: 'acesso.pontoBasico', sub: 'acesso.pontoBasicoSub' },
  { chave: 'acesso.pontoCsv', sub: 'acesso.pontoCsvSub' },
  { chave: 'acesso.pontoRetencao', sub: 'acesso.pontoRetencaoSub' },
  { chave: 'acesso.pontoUpgrade', sub: 'acesso.pontoUpgradeSub' }
];

/**
 * Moldura das telas de acesso: prova à esquerda, formulário à direita.
 *
 * `proof` escolhe o conteúdo do painel — 'runs' no login, para quem volta, e
 * 'points' no cadastro, para quem ainda está decidindo.
 */
const AuthLayout = ({ children, proof = 'runs', headline }) => {
  const { t } = useIdioma();

  return (
    <div className="auth">
      <aside className="auth-aside">
        <div className="sp-grid-bg" />
        <div className="sp-glow auth-aside-glow" />

        <Link to="/" className="auth-brand">
          <Logo size={28} />
        </Link>

        <div className="auth-pitch">
          {/* Frase de efeito, não título de seção: como h2 ela vinha antes do h1
              do formulário e invertia a hierarquia da página. */}
          <p className="auth-pitch-titulo">{headline}</p>

          {proof === 'runs' && (
            <>
              <p>{t('acesso.provaRetencao')}</p>
              <div className="auth-runs">
                {RUNS.map((r) => (
                  <div className="auth-run" key={r.id}>
                    <span className="auth-run-id">{r.id}</span>
                    <span className="auth-run-name">{r.name}</span>
                    <span className="auth-run-n">{r.n}</span>
                    <span className={`sp-tag sp-tag-${r.tone}`}>{t(r.status)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {proof === 'points' && (
            <div className="auth-points">
              {POINTS.map((p) => (
                <div className="auth-point" key={p.chave}>
                  <Icon name="check" size={17} />
                  <div>
                    <b>{t(p.chave)}</b>
                    <span>{t(p.sub)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-box">
          <div className="auth-mobile-brand">
            <div className="sp-grid-bg" />
            <Logo size={40} wordmark={false} />
            <Logo size={28} brackets={false} wordmark />
          </div>
          {children}

          {/* As páginas jurídicas sempre foram rotas públicas, mas o único
              caminho até elas era o rodapé — que existe na home e na tela de
              planos, e em nenhuma das cinco telas de acesso. Quem chegava
              direto no cadastro não tinha como ler os termos que estava
              aceitando sem sair do fluxo e adivinhar a URL. */}
          <div className="auth-legal">
            <Link to="/privacy-policy">{t('site.privacidade')}</Link>
            <i />
            <Link to="/terms-of-service">{t('site.termos')}</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthLayout;
